import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { RotateCcw } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import useEditorAutoGrow from './useEditorAutoGrow';

const shuffleWithSeed = (array, seed) => {
  const result = [...array];
  const rand = (s) => {
    const x = Math.sin(s) * 10000;
    return x - Math.floor(x);
  };
  let seedValue = seed;
  for (let i = result.length - 1; i > 0; i--) {
    seedValue++;
    const j = Math.floor(rand(seedValue) * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  // Guarantee the shuffle isn't identical to the original ranking.
  let attempts = 0;
  while (
    attempts < 6 &&
    result.length > 1 &&
    result.every((it, i) => it.id === array[i].id)
  ) {
    seedValue += 101;
    attempts++;
    for (let i = result.length - 1; i > 0; i--) {
      seedValue++;
      const j = Math.floor(rand(seedValue) * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
  }
  return result;
};

const RankElement = ({
  element,
  isEditor = false,
  studentView = false,
  placements: externalPlacements = null,
  showResult = false,
  perItemResults = null,
  onPlacementsChange,
  onRetry,
  disabled = false,
  onAutoGrow,
}) => {
  const { t } = useTranslation();
  const { content, style, id } = element;
  const {
    layout = 'vertical',
    items = [],
    topLabel = '',
    bottomLabel = '',
    allowRetry = false,
    fontSize = 16,
    fontFamily = 'Inter',
    itemColor = '#ffffff',
    itemTextColor = '#1f2937',
    slotColor = '#2A4D25',
    showNumbers = true,
  } = content || {};
  const backgroundColor = style?.backgroundColor || '#ffffff';

  const shuffledItems = useMemo(() => {
    if (!items || items.length === 0) return [];
    const seed = id ? id.split('').reduce((a, c) => a + c.charCodeAt(0), 0) : 1;
    return shuffleWithSeed(items, seed);
  }, [items, id]);

  // placements: { [itemId]: slotIndex | null }
  const [placements, setPlacements] = useState(externalPlacements || {});
  useEffect(() => {
    if (externalPlacements) setPlacements(externalPlacements);
  }, [externalPlacements]);

  const [draggedItemId, setDraggedItemId] = useState(null);
  const [dragOverSlot, setDragOverSlot] = useState(null);
  const pointerDragRef = useRef(null);
  const scrollRef = useRef(null);
  useEditorAutoGrow({
    enabled: isEditor,
    scrollRef,
    currentHeightPct: element.size?.height || 0,
    contentCount: items.length,
    onAutoGrow,
  });

  const placedItemIds = useMemo(
    () => new Set(Object.entries(placements).filter(([, v]) => v !== null && v !== undefined).map(([k]) => k)),
    [placements]
  );
  const itemAtSlot = useCallback((slotIdx) => {
    const entry = Object.entries(placements).find(([, v]) => v === slotIdx);
    return entry ? entry[0] : null;
  }, [placements]);

  const updatePlacements = useCallback((next) => {
    setPlacements(next);
    onPlacementsChange?.(next);
  }, [onPlacementsChange]);

  const dragEnabled = !disabled && !showResult && !isEditor;

  const startNativeDrag = (e, itemId) => {
    if (!dragEnabled) return;
    const item = items.find((it) => it.id === itemId);
    e.dataTransfer.setData('text/plain', itemId);
    e.dataTransfer.effectAllowed = 'move';
    if (item && typeof document !== 'undefined' && e.dataTransfer.setDragImage) {
      const ghost = document.createElement('div');
      ghost.textContent = item.text;
      Object.assign(ghost.style, {
        position: 'fixed',
        top: '-1000px',
        left: '-1000px',
        padding: '6px 14px',
        borderRadius: '9999px',
        border: `2px solid ${slotColor}`,
        background: itemColor,
        color: itemTextColor,
        font: `500 ${fontSize - 2}px "${fontFamily}", Inter, system-ui, sans-serif`,
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      });
      document.body.appendChild(ghost);
      const rect = ghost.getBoundingClientRect();
      e.dataTransfer.setDragImage(ghost, rect.width / 2, rect.height / 2);
      setTimeout(() => { ghost.remove(); }, 0);
    }
    setDraggedItemId(itemId);
  };

  const handleDragEnd = () => {
    setDraggedItemId(null);
    setDragOverSlot(null);
  };

  const handleSlotDragOver = (e, slotIdx) => {
    if (!dragEnabled) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverSlot(slotIdx);
  };

  const handleSlotDragLeave = () => setDragOverSlot(null);

  const dropItemAtSlot = useCallback((itemId, slotIdx) => {
    const existingInSlot = itemAtSlot(slotIdx);
    const prevSlot = placements[itemId];
    const next = { ...placements };
    if (existingInSlot && existingInSlot !== itemId) {
      // Swap: move the previously-placed item to the dragged item's old slot
      // (if it had one), otherwise send it back to the palette.
      next[existingInSlot] = (prevSlot !== undefined && prevSlot !== null) ? prevSlot : null;
    }
    next[itemId] = slotIdx;
    updatePlacements(next);
  }, [placements, itemAtSlot, updatePlacements]);

  const handleSlotDrop = (e, slotIdx) => {
    if (!dragEnabled) return;
    e.preventDefault();
    const itemId = draggedItemId || e.dataTransfer.getData('text/plain');
    if (!itemId) return;
    dropItemAtSlot(itemId, slotIdx);
    setDraggedItemId(null);
    setDragOverSlot(null);
  };

  const handlePlacedClick = (itemId) => {
    if (!dragEnabled) return;
    if (placements[itemId] === undefined || placements[itemId] === null) return;
    const next = { ...placements };
    next[itemId] = null;
    updatePlacements(next);
  };

  const handlePointerDown = useCallback((e, itemId) => {
    if (!dragEnabled) return;
    if (e.pointerType === 'mouse') return;
    if (pointerDragRef.current) return;
    const item = items.find((it) => it.id === itemId);
    if (!item) return;
    e.preventDefault();

    const ghost = document.createElement('div');
    ghost.textContent = item.text;
    Object.assign(ghost.style, {
      position: 'fixed',
      left: '0',
      top: '0',
      padding: '6px 14px',
      borderRadius: '9999px',
      border: `2px solid ${slotColor}`,
      background: itemColor,
      color: itemTextColor,
      font: `500 ${fontSize - 2}px "${fontFamily}", Inter, system-ui, sans-serif`,
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
      boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
      zIndex: '99999',
      opacity: '0.95',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      willChange: 'transform',
    });
    document.body.appendChild(ghost);
    const rect = ghost.getBoundingClientRect();
    ghost.style.transform = `translate(${e.clientX - rect.width / 2}px, ${e.clientY - rect.height / 2}px)`;

    pointerDragRef.current = { itemId, ghostEl: ghost, pointerId: e.pointerId };
    setDraggedItemId(itemId);
  }, [dragEnabled, items, slotColor, itemColor, itemTextColor, fontFamily, fontSize]);

  useEffect(() => {
    const findSlotAt = (clientX, clientY) => {
      const drag = pointerDragRef.current;
      if (!drag) return null;
      const prevDisplay = drag.ghostEl.style.display;
      drag.ghostEl.style.display = 'none';
      const el = typeof document !== 'undefined' ? document.elementFromPoint(clientX, clientY) : null;
      drag.ghostEl.style.display = prevDisplay;
      const slotEl = el && el.closest ? el.closest('[data-rank-slot-idx]') : null;
      return slotEl ? Number(slotEl.getAttribute('data-rank-slot-idx')) : null;
    };

    const cleanupDrag = () => {
      const drag = pointerDragRef.current;
      if (!drag) return;
      if (drag.ghostEl && drag.ghostEl.parentNode) drag.ghostEl.parentNode.removeChild(drag.ghostEl);
      pointerDragRef.current = null;
      setDraggedItemId(null);
      setDragOverSlot(null);
    };

    const onMove = (e) => {
      const drag = pointerDragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      e.preventDefault();
      const rect = drag.ghostEl.getBoundingClientRect();
      drag.ghostEl.style.transform = `translate(${e.clientX - rect.width / 2}px, ${e.clientY - rect.height / 2}px)`;
      setDragOverSlot(findSlotAt(e.clientX, e.clientY));
    };

    const onUp = (e) => {
      const drag = pointerDragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      const slotIdx = findSlotAt(e.clientX, e.clientY);
      const itemId = drag.itemId;
      cleanupDrag();
      if (slotIdx !== null && slotIdx !== undefined && !Number.isNaN(slotIdx)) {
        dropItemAtSlot(itemId, slotIdx);
      }
    };

    const onCancel = (e) => {
      const drag = pointerDragRef.current;
      if (!drag) return;
      if (e && e.pointerId != null && e.pointerId !== drag.pointerId) return;
      cleanupDrag();
    };

    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
  }, [dropItemAtSlot]);

  useEffect(() => () => {
    const drag = pointerDragRef.current;
    if (drag && drag.ghostEl && drag.ghostEl.parentNode) {
      drag.ghostEl.parentNode.removeChild(drag.ghostEl);
    }
    pointerDragRef.current = null;
  }, []);

  const handleRetry = () => {
    setPlacements({});
    onRetry?.();
  };

  const overallCorrect = perItemResults
    && items.length > 0
    && items.every((it) => perItemResults[it.id] === true);

  const renderItemPill = (item, opts = {}) => {
    const { isPlaced = false, resultValue = null } = opts;
    let bg = itemColor;
    let color = itemTextColor;
    let borderColor = slotColor;
    if (isPlaced && showResult && resultValue !== null) {
      if (resultValue === true) {
        bg = '#dcfce7'; color = '#166534'; borderColor = '#22c55e';
      } else if (resultValue === false) {
        bg = '#fee2e2'; color = '#991b1b'; borderColor = '#ef4444';
      }
    }
    const touchDragEnabled = dragEnabled;
    return (
      <span
        key={item.id + (isPlaced ? '_p' : '_pal')}
        draggable={dragEnabled}
        onDragStart={(e) => startNativeDrag(e, item.id)}
        onDragEnd={handleDragEnd}
        onPointerDown={touchDragEnabled ? (e) => handlePointerDown(e, item.id) : undefined}
        onClick={isPlaced ? () => handlePlacedClick(item.id) : undefined}
        className={`
          inline-flex items-center justify-center rounded-full font-medium border-2
          whitespace-nowrap align-middle select-none
          ${dragEnabled ? 'cursor-grab active:cursor-grabbing' : ''}
          ${isPlaced && !showResult && !disabled && !isEditor ? 'cursor-pointer' : ''}
          ${draggedItemId === item.id ? 'opacity-40' : ''}
          transition-colors duration-100
        `}
        style={{
          backgroundColor: bg,
          color,
          borderColor,
          fontSize: `${fontSize - 2}px`,
          fontFamily,
          padding: '4px 12px',
          WebkitUserDrag: dragEnabled ? 'element' : 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          touchAction: touchDragEnabled ? 'none' : undefined,
        }}
      >
        {item.text}
      </span>
    );
  };

  const numSlots = items.length;
  const isHorizontal = layout === 'horizontal';

  const renderSlot = (slotIdx) => {
    const placedId = itemAtSlot(slotIdx);
    const placedItem = placedId ? items.find((it) => it.id === placedId) : null;
    const isOver = dragOverSlot === slotIdx;
    const posNumber = slotIdx + 1;
    // For horizontal staircase: step up from left to right.
    // Each slot's top offset is proportional to its reverse index.
    const stepHeight = isHorizontal && numSlots > 1
      ? `${((numSlots - 1 - slotIdx) * 10)}px`
      : '0px';

    return (
      <div
        key={`slot_${slotIdx}`}
        data-rank-slot-idx={dragEnabled ? slotIdx : undefined}
        onDragOver={(e) => handleSlotDragOver(e, slotIdx)}
        onDragLeave={handleSlotDragLeave}
        onDrop={(e) => handleSlotDrop(e, slotIdx)}
        className={`flex items-center gap-2 rounded-lg border-2 border-dashed transition-colors ${
          isHorizontal ? 'flex-col px-2 py-2 min-w-[80px]' : 'px-2 py-1.5'
        }`}
        style={{
          borderColor: slotColor,
          backgroundColor: isOver ? `${slotColor}22` : `${slotColor}0D`,
          marginTop: isHorizontal ? stepHeight : undefined,
          minHeight: isHorizontal ? '56px' : '40px',
          minWidth: isHorizontal ? '80px' : '160px',
        }}
      >
        {showNumbers && (
          <span
            className={`inline-flex items-center justify-center rounded-full font-semibold text-white flex-shrink-0 ${
              isHorizontal ? 'w-6 h-6' : 'w-7 h-7'
            }`}
            style={{
              backgroundColor: slotColor,
              fontFamily,
              fontSize: `${Math.max(fontSize - 4, 11)}px`,
            }}
          >
            {posNumber}
          </span>
        )}
        <div className={`flex-1 ${isHorizontal ? 'flex items-center justify-center' : 'flex items-center'}`}>
          {placedItem ? (
            renderItemPill(placedItem, {
              isPlaced: true,
              resultValue: perItemResults?.[placedItem.id] ?? null,
            })
          ) : (
            <span className="text-xs text-gray-300 italic">—</span>
          )}
        </div>
      </div>
    );
  };

  const paletteItems = shuffledItems.filter((it) => !placedItemIds.has(it.id));

  const renderPalette = () => (
    <div className="flex flex-wrap gap-1.5 justify-center items-center min-h-[32px]">
      {paletteItems.length === 0 ? (
        <span className="text-xs text-gray-400 py-1">{t('games.rank.allRanked')}</span>
      ) : (
        paletteItems.map((item) => renderItemPill(item))
      )}
    </div>
  );

  // ---- EDITOR PREVIEW ----
  if (isEditor) {
    return (
      <div
        className="w-full h-full flex flex-col rounded-xl border-2 border-gray-200 overflow-hidden shadow-sm"
        style={{ backgroundColor }}
      >
        <div ref={scrollRef} className="flex-1 min-h-0 p-3 overflow-auto">
          {items.length === 0 ? (
            <p className="text-sm text-gray-400 text-center">{t('games.rank.addItemsInSettings')}</p>
          ) : (
            <div className={`flex ${isHorizontal ? 'flex-row items-end justify-center gap-2' : 'flex-col items-stretch gap-2'}`}>
              {topLabel && !isHorizontal && (
                <p className="text-xs font-semibold text-center" style={{ color: slotColor, fontFamily }}>
                  {topLabel}
                </p>
              )}
              {items.map((item, idx) => {
                const posNumber = idx + 1;
                const stepHeight = isHorizontal && items.length > 1
                  ? `${((items.length - 1 - idx) * 10)}px`
                  : '0px';
                return (
                  <div
                    key={item.id}
                    className={`flex items-center gap-2 rounded-lg border-2 border-dashed ${
                      isHorizontal ? 'flex-col px-2 py-2 min-w-[80px]' : 'px-2 py-1.5'
                    }`}
                    style={{
                      borderColor: slotColor,
                      backgroundColor: `${slotColor}0D`,
                      marginTop: isHorizontal ? stepHeight : undefined,
                      minHeight: isHorizontal ? '56px' : '40px',
                    }}
                  >
                    {showNumbers && (
                      <span
                        className={`inline-flex items-center justify-center rounded-full font-semibold text-white flex-shrink-0 ${
                          isHorizontal ? 'w-6 h-6' : 'w-7 h-7'
                        }`}
                        style={{
                          backgroundColor: slotColor,
                          fontFamily,
                          fontSize: `${Math.max(fontSize - 4, 11)}px`,
                        }}
                      >
                        {posNumber}
                      </span>
                    )}
                    <span
                      className="inline-block px-2 py-0.5 rounded-full border-2"
                      style={{
                        backgroundColor: itemColor,
                        color: itemTextColor,
                        borderColor: slotColor,
                        fontFamily,
                        fontSize: `${fontSize - 2}px`,
                      }}
                    >
                      {item.text || t('games.rank.itemFallback', { index: idx + 1 })}
                    </span>
                  </div>
                );
              })}
              {bottomLabel && !isHorizontal && (
                <p className="text-xs font-semibold text-center" style={{ color: slotColor, fontFamily }}>
                  {bottomLabel}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- INTERACTIVE ----
  return (
    <div
      className="w-full h-full flex flex-col rounded-xl overflow-hidden"
      style={{ backgroundColor: 'transparent' }}
    >
      <div className="flex-1 min-h-0 p-3 overflow-auto flex flex-col gap-2">
        {/* Palette */}
        {renderPalette()}

        {/* Slots */}
        {items.length === 0 ? (
          <p className="text-sm text-gray-400 text-center">{t('games.rank.addItemsInSettings')}</p>
        ) : (
          <div className={`flex ${isHorizontal ? 'flex-row items-end justify-center gap-2' : 'flex-col items-stretch gap-2'}`}>
            {topLabel && !isHorizontal && (
              <p className="text-xs font-semibold text-center" style={{ color: slotColor, fontFamily }}>
                {topLabel}
              </p>
            )}
            {Array.from({ length: numSlots }).map((_, idx) => renderSlot(idx))}
            {bottomLabel && !isHorizontal && (
              <p className="text-xs font-semibold text-center" style={{ color: slotColor, fontFamily }}>
                {bottomLabel}
              </p>
            )}
          </div>
        )}
      </div>

      {showResult && !overallCorrect && allowRetry && (
        <div className="px-3 py-2 flex items-center justify-end">
          <button
            onClick={handleRetry}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition"
          >
            <RotateCcw size={14} />
            {t('games.common.tryAgain')}
          </button>
        </div>
      )}
    </div>
  );
};

export const validateRank = (items = [], placements = {}) => {
  const perItemResults = {};
  let correctCount = 0;
  items.forEach((it, idx) => {
    const placedAt = placements?.[it.id];
    const isCorrect = placedAt === idx;
    perItemResults[it.id] = isCorrect;
    if (isCorrect) correctCount += 1;
  });
  return {
    perItemResults,
    correctCount,
    total: items.length,
    isCorrect: items.length > 0 && correctCount === items.length,
  };
};

export default RankElement;
